import { useId, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { StatusPill } from '@/components/px/StatusPill'
import { canAccess } from '@/access/roleAccess'
import { selectActiveRoleKey } from '@/features/auth/slices/sessionSlice'
import { useAppSelector } from '@/store'
import { makeSelectBoardColumns, selectCanPullToBoard } from '../slices/boardSlice'
import { BOARD_COLUMNS, type EpicFilter } from '../types'
import { TaskCard } from './TaskCard'

export interface TasksViewProps {
  /** The project the cards' requirement links target (route param, or the board's project on /my). */
  projectId: string
  /** /my boards: only the caller's rows (assignee OR reviewer). */
  narrowToCaller: boolean
  /** The URL's epic filter; ignored when `allowFilter` is false. */
  epicFilter: EpicFilter
  /** /my boards carry no epic filter (Decision D1). */
  allowFilter: boolean
}

const ALL: EpicFilter = { kind: 'all' }

/**
 * The four-column Tasks view (CR-BUILD-BOARD-EPICS-2026-09-26, AC-18): Backlog
 * · Build queue · Review (UAT) · Done, always all four with their counts —
 * the board-level empty state is `BuildQueue`'s, never this view's. Which row
 * lands in which column is `makeSelectBoardColumns`' decision (INV-R.1).
 */
export function TasksView({ projectId, narrowToCaller, epicFilter, allowFilter }: TasksViewProps) {
  const { t } = useTranslation('projectWorkspace')
  const headingId = useId()
  // One memoised selector per mounted view, so two boards never evict each other's cache.
  const selectColumns = useMemo(() => makeSelectBoardColumns(), [])
  const filter = allowFilter ? epicFilter : ALL
  const columns = useAppSelector((s) => selectColumns(s, { narrowToCaller, epicFilter: filter }))
  const role = useAppSelector(selectActiveRoleKey)
  const canOpenRegister = canAccess(role, `/project/${projectId}/requirements`)
  // Pull to board (fix cycle 1): this board's project, open with WRITE — never on /my.
  const canPull = useAppSelector((s) => selectCanPullToBoard(s, projectId))

  return (
    <div data-testid="build-queue-columns" className="grid items-start gap-3 md:grid-cols-2 xl:grid-cols-4">
      {BOARD_COLUMNS.map((key) => {
        const rows = columns[key]
        return (
          <section
            key={key}
            aria-labelledby={`${headingId}-${key}`}
            data-testid={`build-queue-column-${key}`}
            data-column={key}
            className="overflow-hidden rounded-lg border border-border bg-card"
          >
            <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
              <h3 id={`${headingId}-${key}`} className="text-[0.8125rem] font-semibold text-foreground">
                {t(`board.columns.${key}`)}
              </h3>
              {key === 'review' && rows.length > 0 ? (
                <StatusPill tone="review">
                  <span data-testid={`build-queue-count-${key}`} className="v-figure">
                    {rows.length}
                  </span>
                </StatusPill>
              ) : (
                <span
                  data-testid={`build-queue-count-${key}`}
                  className="v-figure min-w-6 rounded-full bg-muted px-2 text-center text-xs text-muted-foreground"
                >
                  {rows.length}
                </span>
              )}
            </div>
            {rows.length === 0 ? (
              <p className="px-3 py-6 text-center text-xs text-muted-foreground">{t('board.columns.empty')}</p>
            ) : (
              rows.map((task) => (
                <TaskCard
                  key={task.legacyId}
                  task={task}
                  column={key}
                  projectId={projectId}
                  canOpenRegister={canOpenRegister}
                  canPull={canPull}
                />
              ))
            )}
          </section>
        )
      })}
    </div>
  )
}
